/**
 * Match-engine client — the ONLY way apps/player talks to the match engine
 * (contracts v0.3 surface, docs D-010/D-011).
 *
 * - Base URL: VITE_MATCH_URL (default http://localhost:8790).
 * - AUTH (D-010): every call carries `Authorization: Bearer <supabase_jwt>` —
 *   the D-009 token off the stored auth session (platform auth.getSession()).
 *   No token ⇒ MatchApiError("not_authenticated") BEFORE any network I/O.
 * - Every request body AND every response body is zod-validated against
 *   @matchday/contracts at the boundary — a drifted server payload throws
 *   MatchApiError("bad_response") at the call site, never lands in the UI.
 * - Server errors (contracts ApiError shape) surface as MatchApiError with
 *   the documented code (`snapped`, `max_hosted`, `bad_link`, …).
 *
 * Path map (verified against services/match-engine/src/service.ts):
 *   GET  /feed?night={code}        → FeedResponse (night CODE, not id)
 *   POST /match/create             → CreateMatchRequest  → CreateMatchResponse
 *   POST /match/claim              → ClaimRequest        → ClaimResponse
 *   POST /match/link-opened        → LinkOpenedRequest   → {match} (ignored)
 *   POST /match/withdraw           → WithdrawRequest     → {match} (ignored)
 *   GET  /match/{id}               → MatchStateResponse
 *   POST /match/result             → SubmitResultRequest → {match: Match}
 *   POST /match/verify             → VerifyResultRequest → {match: Match}
 *   POST /match/chat               → ChatPostRequest     → ChatMessageOut
 *   GET  /match/{id}/chat          → ChatMessageOut[]
 *   GET  /leaderboard?night={code} → LeaderboardResponse (night CODE, not id)
 *   POST /find-match/subscribe     → FindMatchSubscribeRequest → FindMatchStatusView
 *   GET  /find-match/status        → FindMatchStatusView (404 no_active_subscription)
 *   POST /find-match/cancel        → {} (WHISTLE CHECK §4)
 *
 * CONTRACT-CHANGE REQUESTS FILED (see BUILDER-feed report):
 *   1. No release endpoint for a CLAIMED hosted match (PULLED UP escape hatch)
 *      — withdraw is the documented interim.
 *   2. No self-identity endpoint (GET /me → PlayerIdentity) — the takeover's
 *      HOME card renders "YOU" until one exists.
 *   3. The {match: Match} submit/verify/withdraw/link-opened envelope should
 *      be ratified in packages/contracts/src/api.ts (engine parses it today).
 */
import {
  ApiError,
  ChatMessageOut,
  ChatPostRequest,
  ClaimRequest,
  ClaimResponse,
  CreateMatchRequest,
  CreateMatchResponse,
  FeedResponse,
  FindMatchStatusView,
  FindMatchSubscribeRequest,
  LeaderboardResponse,
  MatchHistoryResponse,
  ActiveMatchResponse,
  TickerResponse,
  LinkOpenedRequest,
  Match,
  MatchStateResponse,
  PlayerIdentity,
  SubmitResultRequest,
  VarRoomResponse,
  VerifyResultRequest,
  ReportChatMessageRequest,
  WithdrawRequest,
} from "@matchday/contracts";
import { z } from "zod";
import { auth } from "./platform";

export const MATCH_URL: string =
  (import.meta.env?.VITE_MATCH_URL as string | undefined) ?? "http://localhost:8790";

/**
 * Where the entered night code lives (Landing writes it; feed/wizard read
 * it — the engine surface is night-scoped).
 *
 * WHISTLE CHECK §1 (real-device P0, 24 Jul): this was `sessionStorage`, which
 * iOS reclaims the moment the tab's process is killed under memory pressure
 * — exactly what happens backgrounding to EA FC. The auth session survived
 * (it was already `localStorage`); the night code didn't, so the app came
 * back treating an authenticated player as codeless. Moved to `localStorage`
 * — same tier as the session it's meant to travel with. ALWAYS read/write it
 * through `nightCodeStorage` below, never `sessionStorage`/`localStorage`
 * directly, so this invariant can't silently regress one call site at a time.
 */
export const NIGHT_CODE_KEY = "matchday.night_code";

/** The one place NIGHT_CODE_KEY touches browser storage. */
export const nightCodeStorage = {
  get(): string | null {
    try {
      return localStorage.getItem(NIGHT_CODE_KEY);
    } catch {
      return null; // private mode — the no-night guard + recovery cover it
    }
  },
  set(code: string): void {
    try {
      localStorage.setItem(NIGHT_CODE_KEY, code);
    } catch {
      // private mode — nothing persists this tab; recovery/re-entry cover it
    }
  },
};

/**
 * The EA share/paste-in-flight payload — the OPEN PLAY host wizard's W3 relay
 * (MATCH POINT §5.5). Same fix as NIGHT_CODE_KEY, same reason: this must
 * survive a trip out to EA FC and back, so it lives in `localStorage`, not
 * `sessionStorage`. Canonical here (was duplicated as a hand-synced string
 * literal across ShareRelay.tsx and HostWizard.tsx — one source now).
 */
export const PENDING_SHARE_KEY = "matchday.pending_share";

export const pendingShareStorage = {
  get(): string | null {
    try {
      return localStorage.getItem(PENDING_SHARE_KEY);
    } catch {
      return null;
    }
  },
  set(value: string): void {
    try {
      localStorage.setItem(PENDING_SHARE_KEY, value);
    } catch {
      // private mode — the wizard's paste/typed fallbacks still work
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(PENDING_SHARE_KEY);
    } catch {
      // nothing to clear
    }
  },
};

export class MatchApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: unknown,
    public readonly retryAfterS?: number,
  ) {
    super(`match api error ${status}: ${code}`);
    this.name = "MatchApiError";
  }
}

/** GET /match/{id}/chat — the ratified {messages} envelope (BREAK-1 reconciled;
 *  contracts v0.4 ChatListResponse — the engine's shape won, the client unwraps). */
const ChatListResponse = z.object({ messages: z.array(ChatMessageOut) });

/** Engine envelope for submit/verify (contract-change request #3). */
const MatchEnvelope = z.object({ match: Match });
const CurrentNightResponse = z.object({ code: z.string().min(1) });
export type MatchEnvelope = z.infer<typeof MatchEnvelope>;

export interface MatchApiOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** The D-009 bearer source. Default: stored session's supabase_jwt. */
  getToken?: () => Promise<string | null>;
}

export interface MatchApi {
  /** Takes the night CODE (e.g. "BLG-KICK") — the feed is night-scoped. */
  getFeed(nightCode: string): Promise<FeedResponse>;
  createMatch(req: CreateMatchRequest): Promise<CreateMatchResponse>;
  claimMatch(req: ClaimRequest): Promise<ClaimResponse>;
  linkOpened(req: LinkOpenedRequest): Promise<void>;
  withdraw(req: WithdrawRequest): Promise<void>;
  getMatch(matchId: string): Promise<MatchStateResponse>;
  /** POST /match/result → engine {match} envelope → refreshed single truth. */
  submitResult(req: SubmitResultRequest): Promise<MatchStateResponse>;
  /** POST /match/verify → engine {match} envelope → refreshed single truth. */
  verifyResult(req: VerifyResultRequest): Promise<MatchStateResponse>;
  postChat(req: ChatPostRequest): Promise<ChatMessageOut>;
  getChat(matchId: string): Promise<ChatMessageOut[]>;
  /** SCREEN 05 §Report — hide for the reporter, queue for ops. Idempotent. */
  reportChatMessage(req: ReportChatMessageRequest): Promise<void>;
  /** POST /match/release — the PULLED UP escape (host, CLAIMED only; v0.4). */
  release(matchId: string): Promise<Match>;
  /** POST /match/no-show — "they did not get off the bus" (CLAIMED/IN_GAME). */
  reportNoShow(matchId: string): Promise<Match>;
  /** GET /me — the caller's own PlayerIdentity (v0.4). */
  getMe(): Promise<PlayerIdentity>;
  /** Takes the night CODE (e.g. "BLG-KICK") — engine resolves it. */
  getLeaderboard(nightCode: string): Promise<LeaderboardResponse>;
  /** GET /var/room — UPSTAIRS V2: the caller's OWN review room (three stacks).
   *  Not to be confused with the operator-only /var/queue. */
  getVarRoom(): Promise<VarRoomResponse>;
  /** GET /match/history — the player's resolved matches, newest first, with the
   *  W/D/L record. SCREEN 05's verified strip is the way in. */
  getMatchHistory(): Promise<MatchHistoryResponse>;
  /** GET /match/active — the one match still in play, or null. The RESULT tab
   *  reads this so a played match can always be found again. */
  getActiveMatch(): Promise<ActiveMatchResponse>;
  /** GET /ticker — the LIVE strip. An empty list is a real answer. */
  getTicker(): Promise<TickerResponse>;
  /**
   * GET /night/current — WHISTLE CHECK §1 session-continuity recovery: the
   * code of the single OPEN night, when unambiguous. Rejects (`no_current_night`)
   * when zero or multiple nights are OPEN — the caller falls back to the
   * explicit re-entry screen, never a silent guess.
   */
  getCurrentNight(): Promise<{ code: string }>;

  // ── WHISTLE CHECK §4 — FIND ME A MATCH ────────────────────────────────────
  /** POST /find-match/subscribe — may return status "fulfilled" immediately
   *  (a card was already joinable the moment they subscribed). */
  subscribeFindMatch(req: FindMatchSubscribeRequest): Promise<FindMatchStatusView>;
  /** GET /find-match/status — polled by the "FINDING YOU A MATCH" screen.
   *  Throws MatchApiError("no_active_subscription", 404) when the player has
   *  never subscribed (a genuinely clean slate, not an error state to show). */
  getFindMatchStatus(): Promise<FindMatchStatusView>;
  /** POST /find-match/cancel — leaving the "FINDING YOU A MATCH" screen. */
  cancelFindMatch(): Promise<void>;
}

/** Default token source: the stored auth session's D-009 supabase_jwt. */
async function sessionToken(): Promise<string | null> {
  const session = await auth.getSession();
  return session?.supabase_jwt ?? null;
}

export function createMatchApi(options: MatchApiOptions = {}): MatchApi {
  const base = (options.baseUrl ?? MATCH_URL).replace(/\/$/, "");
  const fetchImpl = options.fetchImpl ?? fetch;
  const getToken = options.getToken ?? sessionToken;

  async function throwApiError(res: Response): Promise<never> {
    let code = "unknown_error";
    let detail: unknown;
    let retryAfterS: number | undefined;
    try {
      const parsed = ApiError.safeParse(await res.json());
      if (parsed.success) {
        code = parsed.data.error;
        detail = parsed.data.detail;
        retryAfterS = parsed.data.retry_after_s;
      }
    } catch {
      // non-JSON error body — the status + unknown_error is all we have
    }
    throw new MatchApiError(res.status, code, detail, retryAfterS);
  }

  async function call<T>(
    method: "GET" | "POST",
    path: string,
    body: unknown,
    // Input typed `unknown` so T infers the OUTPUT (branded) type — branded
    // schemas have unbranded inputs and z.ZodType<T> would otherwise unify
    // T down to the unbranded shape.
    schema: z.ZodType<T, z.ZodTypeDef, unknown> | null,
    /**
     * `keepalive` lets the request outlive the page that started it. Only one
     * caller needs it — see `linkOpened` — and it is opt-in because keepalive
     * bodies are capped (64KB) and the flag is pointless for calls whose
     * response we actually read.
     */
    opts?: { keepalive?: boolean },
  ): Promise<T> {
    const token = await getToken();
    if (token === null) throw new MatchApiError(0, "not_authenticated");
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    const init: RequestInit = { method, headers };
    if (opts?.keepalive === true) init.keepalive = true;
    if (body !== undefined) {
      headers["content-type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    let res: Response;
    try {
      res = await fetchImpl(`${base}${path}`, init);
    } catch {
      throw new MatchApiError(0, "network_error");
    }
    if (!res.ok) await throwApiError(res);
    if (schema === null) return undefined as T;
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new MatchApiError(res.status, "bad_response", "response body was not JSON");
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      throw new MatchApiError(res.status, "bad_response", parsed.error.issues);
    }
    return parsed.data;
  }

  const getMatch = (matchId: string) =>
    call("GET", `/match/${encodeURIComponent(matchId)}`, undefined, MatchStateResponse);

  return {
    getFeed: (nightCode) =>
      call("GET", `/feed?night=${encodeURIComponent(nightCode)}`, undefined, FeedResponse),
    createMatch: (req) =>
      call("POST", "/match/create", CreateMatchRequest.parse(req), CreateMatchResponse),
    claimMatch: (req) => call("POST", "/match/claim", ClaimRequest.parse(req), ClaimResponse),
    // keepalive: this fires as the player is being sent to EA FC. Without it a
    // browser is free to cancel the in-flight request when the page goes away,
    // and a cancelled arrival is exactly the bug BK hit — the match stays
    // CLAIMED, no SUBMIT RESULT ever appears, and the sweep voids it.
    linkOpened: (req) =>
      call("POST", "/match/link-opened", LinkOpenedRequest.parse(req), null, { keepalive: true }),
    withdraw: (req) => call("POST", "/match/withdraw", WithdrawRequest.parse(req), null),
    getMatch,
    // The engine answers submit/verify with a bare {match} envelope; the
    // Match Centre consumes the FULL MatchStateResponse (its single truth),
    // so both re-fetch it after the write is validated.
    submitResult: async (req) => {
      const { match } = await call(
        "POST",
        "/match/result",
        SubmitResultRequest.parse(req),
        MatchEnvelope,
      );
      return getMatch(match.id);
    },
    verifyResult: async (req) => {
      const { match } = await call(
        "POST",
        "/match/verify",
        VerifyResultRequest.parse(req),
        MatchEnvelope,
      );
      return getMatch(match.id);
    },
    postChat: (req) => call("POST", "/match/chat", ChatPostRequest.parse(req), ChatMessageOut),
    reportChatMessage: async (req) => {
      await call("POST", "/match/chat/report", ReportChatMessageRequest.parse(req), null);
    },
    getChat: async (matchId) =>
      (await call("GET", `/match/${encodeURIComponent(matchId)}/chat`, undefined, ChatListResponse))
        .messages,
    release: async (matchId) => {
      const { match } = await call(
        "POST",
        "/match/release",
        { match_id: matchId },
        MatchEnvelope,
      );
      return match;
    },
    /**
     * NO SHOW — "they did not get off the bus".
     *
     * The server re-checks the one-minute window itself (a client clock is a
     * suggestion, and this ends a match), so a 409 `too_early` here is a real
     * answer to render, not a failure.
     */
    reportNoShow: async (matchId) => {
      const { match } = await call(
        "POST",
        "/match/no-show",
        { match_id: matchId },
        MatchEnvelope,
      );
      return match;
    },
    getMe: () => call("GET", "/me", undefined, PlayerIdentity),
    getLeaderboard: (nightCode) =>
      call("GET", `/leaderboard?night=${encodeURIComponent(nightCode)}`, undefined, LeaderboardResponse),
    getVarRoom: () => call("GET", "/var/room", undefined, VarRoomResponse),
    getMatchHistory: () => call("GET", "/match/history", undefined, MatchHistoryResponse),
    getActiveMatch: () => call("GET", "/match/active", undefined, ActiveMatchResponse),
    getTicker: () => call("GET", "/ticker", undefined, TickerResponse),
    getCurrentNight: () => call("GET", "/night/current", undefined, CurrentNightResponse),
    subscribeFindMatch: (req) =>
      call("POST", "/find-match/subscribe", FindMatchSubscribeRequest.parse(req), FindMatchStatusView),
    getFindMatchStatus: () =>
      call("GET", "/find-match/status", undefined, FindMatchStatusView),
    cancelFindMatch: () => call("POST", "/find-match/cancel", {}, null),
  };
}

/**
 * WHISTLE CHECK §1 — the session-continuity recovery a caller reaches for
 * when `nightCodeStorage.get()` comes up empty (private-mode storage never
 * landed, the user cleared site data, a new device). Silent on success — the
 * whole point is the player is never asked twice in one continuous session.
 * Returns null when genuinely ambiguous/no event running; the CALLER decides
 * what to show then (never a silent guess, per the brief).
 */
export async function recoverNightCode(api: Pick<MatchApi, "getCurrentNight">): Promise<string | null> {
  try {
    const { code } = await api.getCurrentNight();
    nightCodeStorage.set(code);
    return code;
  } catch {
    return null;
  }
}

/** The app-wide instance (Centre routes import this too — one client, one law). */
export const matchApi: MatchApi = createMatchApi();
